package com.shadowings.gero.presentation.component

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.GameConstants
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.component.quiz.AlynFeedbackBox
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.hieroglyphTint
import com.shadowings.gero.presentation.ui.modalScrim
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import com.shadowings.gero.util.SoundPlayer
import gero.composeapp.generated.resources.F34
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.U28
import gero.composeapp.generated.resources.daily_bonus_free_btn
import gero.composeapp.generated.resources.daily_bonus_free_desc_streak_active
import gero.composeapp.generated.resources.daily_bonus_free_desc_streak_zero
import gero.composeapp.generated.resources.daily_bonus_free_hearts
import gero.composeapp.generated.resources.daily_bonus_free_title
import gero.composeapp.generated.resources.daily_bonus_pro_btn
import gero.composeapp.generated.resources.daily_bonus_pro_desc_streak_active
import gero.composeapp.generated.resources.daily_bonus_pro_desc_streak_zero
import gero.composeapp.generated.resources.daily_bonus_pro_title
import gero.composeapp.generated.resources.top_bar_fire_desc
import gero.composeapp.generated.resources.top_bar_heart_desc
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@DevicePreviews
@Preview(name = "Free - Light")
@Composable
private fun DailyBonusDialogFreeLightPreview() {
    AppTheme {
        DailyBonusDialog(
            isFullVersionUnlocked = false,
            userData = UserData(strike = 3),
            onClaim = {}
        )
    }
}

@Preview(name = "Free - Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun DailyBonusDialogFreeDarkPreview() {
    AppTheme {
        DailyBonusDialog(
            isFullVersionUnlocked = false,
            userData = UserData(strike = 0),
            onClaim = {}
        )
    }
}

@DevicePreviews
@Preview(name = "Pro - Light")
@Composable
private fun DailyBonusDialogProLightPreview() {
    AppTheme {
        DailyBonusDialog(
            isFullVersionUnlocked = true,
            userData = UserData(strike = 5),
            onClaim = {}
        )
    }
}

@Preview(name = "Pro - Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun DailyBonusDialogProDarkPreview() {
    AppTheme {
        DailyBonusDialog(
            isFullVersionUnlocked = true,
            userData = UserData(strike = 0),
            onClaim = {}
        )
    }
}

@Composable
fun DailyBonusDialog(
    isFullVersionUnlocked: Boolean,
    userData: UserData = UserData(),
    onClaim: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Box(
        modifier = modifier
            .fillMaxSize()
            .background(modalScrim)
            .clickable(
                indication = null,
                interactionSource = remember { MutableInteractionSource() }
            ) { onClaim() }
            .semantics { testTag = "daily_bonus_overlay" },
        contentAlignment = Alignment.Center
    ) {
        DailyBonusDialogContent(
            isFullVersionUnlocked = isFullVersionUnlocked,
            userData = userData,
            onClaim = onClaim,
            modifier = Modifier
                .fillMaxWidth(0.92f)
                .clickable(
                    indication = null,
                    interactionSource = remember { MutableInteractionSource() }
                ) { /* Consume click inside the card */ }
        )
    }
}

@Composable
fun DailyBonusDialogContent(
    isFullVersionUnlocked: Boolean,
    userData: UserData,
    onClaim: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val deviceInfo = rememberDeviceInfo()
    val typography = MaterialTheme.typography
    val strikeCount = userData.strike

    LaunchedEffect(Unit) {
        SoundPlayer.playStreak()
    }

    ElevatedCard(
        modifier = modifier,
        shape = RoundedCornerShape(24.dp),
    ) {
        Column(
            modifier = Modifier
                .padding(16.dp)
                .verticalScroll(rememberScrollState()),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            // Titolo
            Text(
                text = if (isFullVersionUnlocked) {
                    stringResource(Res.string.daily_bonus_pro_title)
                } else {
                    stringResource(Res.string.daily_bonus_free_title)
                },
                style = typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                textAlign = TextAlign.Center,
                modifier = Modifier.fillMaxWidth()
            )

            // Icona hieroglyph identica alla Top Bar (F34 per cuori, U28 per streak)
            val fontScale = LocalDensity.current.fontScale
            if ((fontScale < 1.49f || deviceInfo.isPortrait) && deviceInfo.deviceClass != DeviceClass.PHONE_SMALL) {
                Box(
                    modifier = Modifier
                        .size(64.dp)
                        .background(
                            color = MaterialTheme.colorScheme.primaryContainer,
                            shape = RoundedCornerShape(16.dp)
                        )
                        .padding(10.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        painter = painterResource(
                            if (isFullVersionUnlocked) Res.drawable.U28 else Res.drawable.F34
                        ),
                        contentDescription = stringResource(
                            if (isFullVersionUnlocked) Res.string.top_bar_fire_desc else Res.string.top_bar_heart_desc
                        ),
                        modifier = Modifier.fillMaxSize(),
                        colorFilter = ColorFilter.tint(hieroglyphTint)
                    )
                }
            }

            // Indicatore bonus cuori in nero / onSurface (solo per utenti Free)
            if (!isFullVersionUnlocked) {
                Text(
                    text = stringResource(
                        Res.string.daily_bonus_free_hearts,
                        GameConstants.DAILY_BONUS_HEARTS
                    ),
                    style = typography.titleMedium.copy(
                        fontWeight = FontWeight.Bold,
                        color = MaterialTheme.colorScheme.onSurface
                    ),
                    textAlign = TextAlign.Center,
                    modifier = Modifier.fillMaxWidth()
                )
            }

            // Descrizione detta da Alyn tramite AlynFeedbackBox
            val descriptionText = if (isFullVersionUnlocked) {
                if (strikeCount > 0) {
                    stringResource(Res.string.daily_bonus_pro_desc_streak_active, strikeCount)
                } else {
                    stringResource(Res.string.daily_bonus_pro_desc_streak_zero)
                }
            } else {
                if (strikeCount > 0) {
                    stringResource(Res.string.daily_bonus_free_desc_streak_active, strikeCount)
                } else {
                    stringResource(Res.string.daily_bonus_free_desc_streak_zero)
                }
            }

            AlynFeedbackBox(
                face = Faces.Happy,
                text = descriptionText,
                userData = userData
            )

            // Bottone di azione
            val buttonTitle = if (isFullVersionUnlocked) {
                stringResource(Res.string.daily_bonus_pro_btn)
            } else {
                stringResource(Res.string.daily_bonus_free_btn)
            }

            GeroButton(
                title = buttonTitle,
                testTagId = "daily_bonus_btn_claim"
            ) {
                onClaim()
            }
        }
    }
}
